import React from 'react';
import { FileText, AlertCircle, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Terms = () => {
  // Format current date as "Month Day, Year"
  const getCurrentDate = () => {
    const date = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-12 lg:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold shadow mb-6">
                <FileText className="h-4 w-4" />
                Terms and Conditions
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Terms and Conditions
              </h1>
              <p className="text-lg text-gray-600">
                {/* Effective Date: {getCurrentDate()} */}
                Effective Date: February 10, 2025
              </p>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 space-y-8">
              {/* Introduction */}
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  These Terms and Conditions ("Terms") govern your access to and use of the mySTORYme website and web application (collectively, the "Service"), which is provided by mySTORYme ("we," "us," or "our"). By accessing or using our Service, you agree to be bound by these Terms. If you do not agree with these Terms, please do not access or use our Service.
                </p>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-yellow-900 mb-2">Important Notice for Parents and Guardians:</h3>
                      <p className="text-yellow-800 text-sm leading-relaxed">
                        While the parent or legal guardian (the "Account Owner") is responsible for subscribing to mySTORYme, creating an account, and setting up story profiles, the primary user of the Service is typically the child for whom the story profile is created. By creating an account for or using the Service for a child under the age of 13, you represent and warrant that you are the child's parent or legal guardian and consent to the collection and use of your child's information as outlined in our Privacy Policy.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 1 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  By registering for an account or using the Service in any manner, you agree to be bound by these Terms, our Privacy Policy (incorporated herein by reference), and any additional policies or guidelines that may be posted on the Service from time to time.
                </p>
              </div>

              {/* Section 2 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">2. Description of the Service</h2>
                <p className="text-gray-700 leading-relaxed">
                  mySTORYme is a personalized storytelling platform that enables parents or legal guardians to create customized story profiles for their children. These profiles, which include details such as the child's name, age, interests, and story themes, are used to generate personalized stories that the child can read or listen to. Although the parent is the Account Owner—responsible for subscribing to the Service and managing account settings—the child will typically be the primary user of the application and consumer of the personalized content provided.
                </p>
              </div>

              {/* Section 3 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">3. Eligibility</h2>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">For Account Owners (Parents/Guardians):</h3>
                    <p className="text-gray-700 leading-relaxed">
                      You must be at least 18 years of age (or the age of majority in your jurisdiction) and possess the legal authority to act on behalf of any child for whom you create an account or set up a story profile.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">For Child Users:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Children under the age of 13 may use the Service only when an account has been created and managed by a parent or legal guardian. By creating an account for a child, you confirm that you are the child's parent or legal guardian and that you have obtained any necessary consents required by applicable laws.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">4. Account Registration and Parental Consent</h2>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Account Creation:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      When you register for an account, you agree to provide accurate, current, and complete information as requested. This includes providing details about your child for whom the story profile is created (such as name, age, date of birth, gender, friends' first names, siblings' and other relatives' first names, story themes, and interests).
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Parental Responsibility:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      As the Account Owner, you are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Parental Consent for Child's Data:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      By providing information regarding your child, you represent and warrant that you are the child's parent or legal guardian and that you consent to our collection, use, and disclosure of such information in accordance with our Privacy Policy.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 5 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">5. Payment, Subscriptions, and Fees</h2>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Subscription Model:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Access to the Service may require a paid subscription. The Account Owner is responsible for all fees and charges incurred under the account. Payment terms, billing cycles, and cancellation policies will be communicated at the time of purchase and are subject to change with notice.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Refunds:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      All fees are non-refundable except as required by law or as otherwise provided in these Terms.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">6. User Obligations</h2>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Accurate Information:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      You agree to provide true, accurate, and complete information when creating and updating your account and your child's story profile.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Compliance:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      You agree to use the Service only for lawful purposes and in accordance with these Terms. You will not use the Service in any manner that could disable, overburden, or impair the Service or interfere with any other party's use of the Service.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Supervision of Child Users:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      As the Account Owner, you agree to supervise your child's use of the Service and to ensure that they adhere to these Terms and any applicable guidelines.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 7 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">7. Intellectual Property Rights</h2>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Ownership:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      All content provided on or through the Service—including but not limited to personalized stories, text, graphics, logos, images, and software—is the property of mySTORYme or its licensors and is protected by applicable intellectual property laws.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">License to Use:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes. This license does not include any resale or commercial use of the Service or its contents.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">User Content:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Any content (such as story profile details) that you submit to the Service remains your property; however, you grant us a non-exclusive, royalty-free, worldwide license to use, reproduce, modify, adapt, publish, and display such content solely for the purpose of operating, promoting, and improving our Service.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 8 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">8. Prohibited Conduct</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  When using our Service, you agree not to:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Violate any applicable laws, regulations, or third-party rights.</li>
                  <li>Engage in any conduct that is harmful, abusive, or offensive.</li>
                  <li>Attempt to bypass, disable, or otherwise interfere with security features or functionalities of the Service.</li>
                  <li>Use automated means (such as bots or scrapers) to access or collect data from the Service.</li>
                  <li>Misrepresent your identity or affiliation with any person or entity.</li>
                  <li>Post or transmit any content that is infringing, defamatory, obscene, or otherwise objectionable.</li>
                </ul>
              </div>

              {/* Section 9 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">9. Third-Party Content and Links</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Service may contain links to third-party websites, services, or resources. We do not control and are not responsible for the availability, content, or policies of these third-party sites. Your use of any third-party websites is at your own risk and subject to the terms and conditions of those websites.
                </p>
              </div>

              {/* Section 10 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">10. Disclaimer of Warranties</h2>
                <p className="text-gray-700 leading-relaxed font-semibold">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.
                </p>
              </div>

              {/* Section 11 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">11. Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed font-semibold">
                  IN NO EVENT SHALL MYSTORYME, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR ACCESS TO OR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT PAID BY YOU, IF ANY, FOR ACCESSING THE SERVICE.
                </p>
              </div>

              {/* Section 12 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">12. Indemnification</h2>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless mySTORYme and its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, or expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your infringement of any rights of a third party.
                </p>
              </div>

              {/* Section 13 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">13. Termination</h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to suspend or terminate your access to the Service, in whole or in part, at any time, without notice, for any reason, including for violation of these Terms. Upon termination, your right to use the Service will immediately cease, and you must promptly delete all content downloaded or otherwise obtained from the Service.
                </p>
              </div>

              {/* Section 14 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">14. Governing Law and Dispute Resolution</h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law principles. Any disputes arising out of or relating to these Terms or the Service shall be resolved exclusively through binding arbitration in accordance with the rules of [Arbitration Institution] or in the appropriate courts located within [Your Jurisdiction], as applicable.
                </p>
              </div>

              {/* Section 15 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">15. Changes to These Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update these Terms from time to time to reflect changes to our practices or for other operational, legal, or regulatory reasons. When we make changes, we will update the "Effective Date" at the top of these Terms and notify you by posting the updated Terms on our website. Your continued use of the Service after any such changes constitutes your acceptance of the new Terms.
                </p>
              </div>

              {/* Section 16 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">16. Miscellaneous</h2>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Severability:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Entire Agreement:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms, together with our Privacy Policy and any other policies or guidelines referenced herein, constitute the entire agreement between you and mySTORYme regarding your use of the Service.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Contact Information:</h3>
                    <p className="text-gray-700 leading-relaxed mb-2">
                      If you have any questions or concerns about these Terms, please contact us at:
                    </p>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-purple-600" />
                        <a href="mailto:support@mystoryme.com" className="text-purple-600 font-semibold hover:text-purple-700">
                          support@mystoryme.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Closing Statement */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 mt-8">
                <p className="text-gray-700 leading-relaxed font-medium">
                  By accessing or using mySTORYme, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;

